import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useAtomValue } from 'jotai'
import { LanguagePicker, type FeedbackLanguage } from '@/components/LanguagePicker'
import { useTar } from '@/modules/i18n/useTar'
import { setStoredLanguage } from '@/modules/i18n/languageStorage'
import { isPrerenderedPath, swapLangInPath } from '@/modules/seo/prerenderedPaths'
import {
  isSupportedLanguage,
  type SupportedLanguage,
} from '@/modules/i18n/supportedLanguages'
import { getSelectableUiLanguages } from '@/modules/i18n/uiLanguageVisibility'
import { germanUiEnabledAtom } from '@/store/debugAtoms'
import { ClientAnalytics } from '@/modules/clientAnalytics'

export function LanguageSwitcher() {
  const { language, changeLanguage } = useTar()
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  const germanEnabled = useAtomValue(germanUiEnabledAtom)
  const selectable = getSelectableUiLanguages(germanEnabled)

  const current: SupportedLanguage =
    isSupportedLanguage(language) && selectable.includes(language) ? language : 'en'

  const handleChange = (next: FeedbackLanguage) => {
    if (!isSupportedLanguage(next) || next === current) return

    setStoredLanguage(next)
    void changeLanguage(next)

    ClientAnalytics.track('language_changed', { from: current, to: next, source: 'switcher' })
    ClientAnalytics.setUserProperties({ language: next })

    if (isPrerenderedPath(pathname)) {
      const targetPath = swapLangInPath(pathname, next)
      if (targetPath !== pathname) {
        void navigate({ to: targetPath })
      }
    }
  }

  return (
    <LanguagePicker
      value={current}
      onValueChange={handleChange}
      options={selectable}
    />
  )
}
